// src/planner/fetchEvents.js

// 🔎 Minimalny logger do debugowania w konsoli (opcjonalny)
if (typeof window !== "undefined") {
    window.__FE_DEBUG = window.__FE_DEBUG || [];
    window._feLog = (payload) => {
        try {
            window.__FE_DEBUG.push({ at: new Date().toISOString(), payload });
        } catch { }
    };
    window.showFetchEventsDebug = () => window.__FE_DEBUG;
}
const _log = (p) => { try { if (window?._feLog) window._feLog(p); } catch { } };

// YYYY-MM-DD w CZASIE LOKALNYM
const localDayKey = (d) => {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

// Iteracja po dniach w CZASIE LOKALNYM (inclusive)
function* eachLocalDateInclusive(start, end) {
    const cur = new Date(start);
    const stop = new Date(end);
    cur.setHours(0, 0, 0, 0);
    stop.setHours(0, 0, 0, 0);
    while (cur.getTime() <= stop.getTime()) {
        yield new Date(cur);
        cur.setDate(cur.getDate() + 1);
    }
}

const norm = (s) => String(s || "").trim().toLowerCase();

export async function fetchEvents(calendarId, token, isoDate, stringToColor, firestoreEvents = {}) {
    const now = new Date();
    const sixMonthsAgo = new Date(now); sixMonthsAgo.setMonth(now.getMonth() - 6);
    const sixMonthsAhead = new Date(now); sixMonthsAhead.setMonth(now.getMonth() + 6);
    const iso = (d) => d.toISOString();

    _log({ tag: "START", calendarId, timeMin: iso(sixMonthsAgo), timeMax: iso(sixMonthsAhead) });

    // puste zwroty w razie braku danych
    const empty = () => ({ events: {}, aliveTriples: new Set(), aliveNameDays: new Set(), cancelledNameDays: new Set() });

    if (!calendarId || !token) return empty();

    try {
        // KLUCZOWE: showDeleted=true + singleEvents=true – łapiemy skasowane instancje
        const url =
            `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
            `?timeMin=${iso(sixMonthsAgo)}&timeMax=${iso(sixMonthsAhead)}` +
            `&singleEvents=true&orderBy=startTime&showDeleted=true`;

        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();

        const out = {};
        const aliveTriples = new Set();      // `${calId}::${gId}::${day}`
        const aliveNameDays = new Set();     // `${calId}::${normName}::${day}`
        const cancelledNameDays = new Set(); // `${calId}::${normName}::${day}`

        const items = Array.isArray(data.items) ? data.items : [];
        _log({ tag: "FETCH_DONE", items: items.length });

        for (const item of items) {
            const gId = item.id || "";
            const name = String(item.summary || "(Bez nazwy)").trim();
            const nName = norm(name);

            // CANCELLED instancje serii
            if (item.status === "cancelled") {
                const orig = item.originalStartTime;
                if (orig?.dateTime || orig?.date) {
                    const d = new Date(orig.dateTime || (orig.date + "T00:00:00"));
                    const keyDay = localDayKey(d);
                    cancelledNameDays.add(`${calendarId}::${nName}::${keyDay}`);
                }
                continue;
            }

            // Wylicz zakres dni w CZASIE LOKALNYM
            const isAllDay =
                !!item.start?.date && !!item.end?.date && !item.start?.dateTime && !item.end?.dateTime;

            let startLocal, endLocalInclusive;
            if (isAllDay) {
                const s = new Date(item.start.date + "T00:00:00");
                const eExcl = new Date(item.end.date + "T00:00:00");
                const eIncl = new Date(eExcl);
                eIncl.setDate(eIncl.getDate() - 1);
                startLocal = s;
                endLocalInclusive = eIncl;
            } else {
                const s = new Date(item.start.dateTime || item.start.date);
                const e = new Date(item.end.dateTime || item.end.date);
                startLocal = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0);
                endLocalInclusive = new Date(e.getFullYear(), e.getMonth(), e.getDate(), 0, 0, 0, 0);
            }

            const addedDays = [];
            for (const d of eachLocalDateInclusive(startLocal, endLocalInclusive)) {
                const key = localDayKey(d);
                addedDays.push(key);

                if (!out[key]) out[key] = [];

                // odczytaj poprzedni wpis (dla staff/needed/kolor)
                const existed =
                    (firestoreEvents[key] || []).find(
                        (e) => e.calendarId === calendarId && e.gId && e.gId === gId
                    ) ||
                    (firestoreEvents[key] || []).find(
                        (e) => e.calendarId === calendarId && norm(e.event) === nName
                    ) ||
                    (firestoreEvents[key] || []).find((e) => norm(e.event) === nName);

                const prevStaff = Array.isArray(existed?.staff) ? existed.staff : [];
                const needed =
                    Number.isFinite(Number(existed?.needed)) && Number(existed.needed) >= 1
                        ? Number(existed.needed)
                        : Math.max(1, prevStaff.length || 0);

                out[key].push({
                    event: name,
                    color: existed?.color || (typeof stringToColor === "function" ? stringToColor(name) : "#888"),
                    staff: prevStaff,
                    needed,
                    calendarId,
                    gId,
                });

                if (gId) aliveTriples.add(`${calendarId}::${gId}::${key}`);
                aliveNameDays.add(`${calendarId}::${nName}::${key}`);
            }

            _log({
                tag: "EVENT_DAYS",
                summary: name,
                count: addedDays.length,
                first: addedDays[0],
                last: addedDays[addedDays.length - 1],
            });
        }

        _log({
            tag: "END",
            totalDayKeys: Object.keys(out).length,
            aliveTriplesSize: aliveTriples.size,
            aliveNameDaysSize: aliveNameDays.size,
            cancelledNameDaysSize: cancelledNameDays.size,
        });

        return { events: out, aliveTriples, aliveNameDays, cancelledNameDays };
    } catch (error) {
        _log({ tag: "ERROR", error: String(error) });
        return empty();
    }
}
